import { DealRow } from './view-config'

/**
 * Seeded deterministic mock dataset shaped like dev.deal_reporting.
 * Same seed -> identical data on every load, so demos are repeatable.
 * Dates are offsets from "now" so time-window views always have data.
 */

/** Captured once at import to ensure full determinism across calls in a test session. */
const NOW = Date.now()

/** mulberry32 — tiny seeded PRNG, good enough for demo data. */
const mulberry32 = (seed: number) => () => {
  seed |= 0
  seed = (seed + 0x6d2b79f5) | 0
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296
}

// Funnel-shaped stage distribution mirroring the real counts in the
// dashboarding Notion doc (many Developing, few Finalized Pricing).
const STAGES: { name: string; order: number; weight: number }[] = [
  { name: 'Target', order: 0, weight: 0.12 },
  { name: 'Interested', order: 1, weight: 0.16 },
  { name: 'Developing', order: 2, weight: 0.25 },
  { name: 'Finalized Pricing', order: 3, weight: 0.07 },
  { name: 'Closed Won - Pending PO', order: 4, weight: 0.04 },
  { name: 'Closed Won', order: 5, weight: 0.21 },
  { name: 'Closed Lost', order: 6, weight: 0.12 },
  { name: 'Churn', order: 7, weight: 0.03 },
]

/** Sales stages in pipeline order — drives group ordering for `sales_stage`. */
export const STAGE_ORDER: string[] = STAGES.map((s) => s.name)

/** Account owners (reps). Exported so the persona demo can pick a "logged-in" rep. */
export const OWNERS = [
  'Danny Brown',
  'Laura Grace',
  'Ian Bavitz',
  'Phoebe Bridgers',
  'Jeff Rosenstock',
]

const PRODUCTS = [
  'Pickle Smoothie',
  'Pickle Jam',
  'Pickle chips',
  'Pickle ice cream',
]

const CATEGORIES: {
  name: string
  slug: string
  weight: number
  subs: string[]
}[] = [
  {
    name: 'Commercial',
    slug: 'commercial',
    weight: 0.55,
    subs: ['Restaurants & Bars', 'Other'],
  },
  { name: 'Retail', slug: 'retail', weight: 0.15, subs: ['Grocery Stores'] },
  {
    name: 'Non-commercial',
    slug: 'non-commercial',
    weight: 0.2,
    subs: ['Education', 'Healthcare', 'Hospitality'],
  },
  {
    name: 'Intermediaries',
    slug: 'intermediaries',
    weight: 0.1,
    subs: ['Wholesalers and Distributors'],
  },
]

// Recognizable names from the Notion examples + filler.
const COMPANIES = [
  'Habit Burger & Grill',
  "Culver's",
  'IHOP',
  'Cracker Barrel Old Country Store',
  "Hardee's",
  'Lidl',
  'Checkers',
  'Hilton',
  'Bob Evans',
  'Starbucks Coffee Company',
  'Kroger Deli',
  "Ward's Restaurant",
  'Street Operators',
  'Jetro Restaurant Depot',
  'Cold Beers & Cheeseburgers',
  'Waffle House',
  'Sheetz',
  "Buc-ee's",
  "Portillo's",
  'In-N-Out Burger',
  "Raising Cane's",
  'Five Guys',
  'Shake Shack',
  'White Castle',
  "Jersey Mike's",
  'Texas Roadhouse',
  "Denny's",
  'Golden Corral',
  'Sizzler',
  'Aramark Campus Dining',
  'Sodexo East',
  'Compass Group Midwest',
  'HCA Healthcare Dining',
  'Marriott Banquets',
  'Hyatt F&B',
  'Sysco Direct',
  'US Foods Local',
  'Gordon Food Service',
  'Performance Food Group',
  'Ben E. Keith',
]

const DISTRIBUTORS = [
  'TriMark',
  'Singer',
  'Ed Don',
  'Wasserstrom',
  'Clark Associates',
]
const GPOS = ['Foodbuy', 'Premier', 'Vizient', 'Entegra']

const DAY_MS = 86_400_000

const pickWeighted = <T extends { weight: number }>(
  rand: () => number,
  items: T[]
): T => {
  let r = rand()
  for (const item of items) {
    r -= item.weight
    if (r <= 0) return item
  }
  return items[items.length - 1]
}

const pick = <T>(rand: () => number, items: T[]): T =>
  items[Math.floor(rand() * items.length)]

/** Log-skewed positive number: few whales, long tail. */
const skewed = (rand: () => number, scale: number): number =>
  Math.round(Math.exp(rand() * 3.5) * scale)

export function generateDeals(count: number, seed: number): DealRow[] {
  const rand = mulberry32(seed)
  const rows: DealRow[] = []

  for (let i = 0; i < count; i++) {
    const stage = pickWeighted(rand, STAGES)
    const cat = pickWeighted(rand, CATEGORIES)
    const isClosed = ['Closed Won', 'Closed Lost', 'Churn'].includes(stage.name)
    // Win/loss flag for win-rate (avg of is_won skips open deals' nulls, so
    // avg = Won / (Won + Lost) automatically). Pending-PO counts as won.
    const isWon = stage.name.startsWith('Closed Won')
    const isLost = stage.name === 'Closed Lost' || stage.name === 'Churn'
    const wonFlag = isWon ? 1 : isLost ? 0 : null
    // Closed deals spread over the last 8 quarters so by-quarter charts have shape.
    const closedAt = isClosed ? NOW - rand() * 730 * DAY_MS : null
    const createdAt = (closedAt ?? NOW) - (30 + rand() * 300) * DAY_MS
    const hasRevenue = rand() > 0.15 // ~15% revenue holes, like real data
    const monthlyVolume = skewed(rand, 2500)
    const monthlyRevenue = hasRevenue
      ? Math.round(monthlyVolume * (2 + rand() * 3))
      : null

    rows.push({
      deal_id: 100_000 + i,
      account_owner: pick(rand, OWNERS),
      company_name:
        i < COMPANIES.length ? COMPANIES[i] : `${pick(rand, COMPANIES)} #${i}`,
      category: cat.name,
      category_slug: cat.slug,
      sub_category: pick(rand, cat.subs),
      product: pick(rand, PRODUCTS),
      sales_stage: stage.name,
      stage_order: stage.order,
      is_won: wonFlag,
      days_in_stage: Math.round(rand() * (isClosed ? 365 : 120)),
      // Days since the deal's last logged activity — drives the "stale deals"
      // report. Spread 0–90 so a healthy chunk of open deals reads as stale.
      days_since_update: Math.round(rand() * 90),
      datetime_closed: closedAt ? new Date(closedAt).toISOString() : null,
      close_date_override:
        !isClosed && rand() > 0.4
          ? new Date(NOW + rand() * 180 * DAY_MS).toISOString().slice(0, 10)
          : null,
      created: new Date(createdAt).toISOString(),
      note_count: Math.floor(rand() * 8),
      contact_count: Math.floor(rand() * 5),
      distributor_names:
        rand() > 0.5
          ? [pick(rand, DISTRIBUTORS)]
          : rand() > 0.7
            ? [pick(rand, DISTRIBUTORS), pick(rand, DISTRIBUTORS)]
            : [],
      gpo_names: rand() > 0.75 ? [pick(rand, GPOS)] : [],
      monthly_volume: monthlyVolume,
      monthly_revenue: monthlyRevenue,
      annual_volume: monthlyVolume * 12,
      annual_revenue: monthlyRevenue === null ? null : monthlyRevenue * 12,
    })
  }
  return rows
}

/** Canonical dataset used by the UI. Fixed seed -> repeatable demos. */
export const MOCK_DEALS: DealRow[] = generateDeals(800, 42)
